import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import entityComposition from '../common/classes/entityComposition';
import FormEditor from './formEditor';
import Loader from '../common/loader';
import { formTypePermissionSelector } from '../common/classes/permissions';
import appHistory from '../../utils/appHistory';
import { loadDirectFormSession, normalizeDirectFormId, saveDirectFormSession } from './directFormSession';
const PERSIST_DEBOUNCE_MS = 300;
const DirectFormContainer = ({
  form: {
    active,
    active: {
      content: {
        title
      } = {},
      id: activeId
    } = {},
    pendingChanges: _pendingChanges = false
  } = {
    active: {
      isNew: false
    }
  },
  formActions: _formActions = {},
  match: {
    params: {
      id
    }
  }
}) => {
  const routeIdNorm = normalizeDirectFormId(id);
  const activeIdNorm = normalizeDirectFormId(activeId);
  const hasStaleForm = Boolean(routeIdNorm && activeIdNorm && routeIdNorm !== activeIdNorm);
  // Track which route id has been cold-load handled (instead of a permanent
  // boolean latch) so restore/redirect re-runs when the route :id changes
  // while the component stays mounted (in-app nav, browser back/forward).
  const coldLoadHandledIdRef = useRef(null);
  const [awaitingRestore, setAwaitingRestore] = useState(() => Boolean(routeIdNorm && !activeIdNorm));

  // Re-arm the loader state when the route or active id changes while mounted.
  useEffect(() => {
    setAwaitingRestore(Boolean(routeIdNorm && !activeIdNorm));
  }, [routeIdNorm, activeIdNorm]);

  // Another screen (e.g. cabinet form) may still own form.active — e.g. after
  // browser back to /directForms/:id. Clear so we do not render the wrong form.
  useEffect(() => {
    if (hasStaleForm && _formActions !== null && _formActions !== void 0 && _formActions.showCancel) {
      _formActions.showCancel();
    }
  }, [hasStaleForm, _formActions]);

  // On cold load (refresh), restore from sessionStorage or fall back to a new form.
  useLayoutEffect(() => {
    if (coldLoadHandledIdRef.current === routeIdNorm || activeIdNorm || hasStaleForm || !routeIdNorm) {
      return;
    }
    coldLoadHandledIdRef.current = routeIdNorm;
    const snapshot = loadDirectFormSession(routeIdNorm);
    if (snapshot && _formActions !== null && _formActions !== void 0 && _formActions.restoreDirectFormSession) {
      _formActions.restoreDirectFormSession(snapshot);
      setAwaitingRestore(false);
      return;
    }
    if (snapshot !== null && snapshot !== void 0 && snapshot.formTypeIdentifier) {
      appHistory.replace(`/directForms/new/${snapshot.formTypeIdentifier}`);
      return;
    }
    setAwaitingRestore(false);
    appHistory.replace('/');
  }, [activeIdNorm, _formActions, hasStaleForm, routeIdNorm]);

  // Debounce persistence so large-form edits don't serialize + write to
  // sessionStorage synchronously on every change.
  const persistRef = useRef(null);
  if (!persistRef.current) {
    persistRef.current = debounce((formId, snapshot) => {
      saveDirectFormSession(formId, snapshot);
    }, PERSIST_DEBOUNCE_MS);
  }

  // Keep sessionStorage in sync while editing a direct form.
  useEffect(() => {
    if (!routeIdNorm || !activeIdNorm || routeIdNorm !== activeIdNorm || !(active !== null && active !== void 0 && active.id)) {
      return undefined;
    }
    persistRef.current(routeIdNorm, {
      active,
      pendingChanges: _pendingChanges
    });
    return undefined;
  }, [active, activeIdNorm, _pendingChanges, routeIdNorm]);

  // Flush pending writes on unmount and on browser unload so a refresh right
  // after an edit still persists the latest snapshot.
  useEffect(() => {
    const persist = persistRef.current;
    const flush = () => persist.flush();
    global.window.addEventListener('beforeunload', flush);
    return () => {
      global.window.removeEventListener('beforeunload', flush);
      persist.flush();
    };
  }, []);

  // Clean up Redux form state when leaving a direct form so the next page
  // (e.g. a regular form editor) starts with a clean slate.
  useEffect(() => () => {
    if (_formActions !== null && _formActions !== void 0 && _formActions.showCancel) {
      _formActions.showCancel();
    }
  }, [_formActions]);
  if (!activeId || hasStaleForm || awaitingRestore) {
    return /*#__PURE__*/React.createElement(Loader, {
      style: {
        marginTop: '50%'
      },
      loaded: false
    });
  }
  return /*#__PURE__*/React.createElement(FormEditor, {
    id: id,
    formTitle: title,
    sectionsLabel: "Sections",
    showSave: false,
    hideBackButton: true,
    related: {
      suppressUnsavedChangesNotification: true
    }
  });
};
DirectFormContainer.propTypes = {
  form: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({
        title: PropTypes.string
      }),
      id: PropTypes.string,
      isNew: PropTypes.bool
    }),
    loading: PropTypes.bool.isRequired,
    pendingChanges: PropTypes.bool.isRequired
  }),
  formActions: PropTypes.shape({
    restoreDirectFormSession: PropTypes.func,
    showCancel: PropTypes.func
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    }),
    url: PropTypes.string
  }).isRequired
};
export { DirectFormContainer as TestDirectFormContainer };
export default entityComposition(DirectFormContainer, ['form'], {
  canView: formTypePermissionSelector('view_detail')
}, []);